import IdGen from '@/utils/idGenerator'
import { formatDate } from '@/utils/common'
import { mergePoundsAndOuncesIntoWeight, splitWeightInPoundsAndOunces } from '@/utils/unitConverters/weightConvertor'

const mapDraftShippingBoxConfiguration = ({
  quantityPerBox,
  numberOfBoxes,
  length,
  width,
  height,
  weight
}) => ({
  quantityPerBox,
  numberOfBoxes,
  length,
  width,
  height,
  weight: splitWeightInPoundsAndOunces(weight)
})

export const mapDraftShippingFromApi = ({
  productId,
  productName,
  lotNumber,
  expirationDate,
  boxConfigurations,
  shipmentQuantity
}, id) => ({
  id,
  productId,
  productName,
  lotNumber: lotNumber ? lotNumber.trim() : null,
  expirationDate: formatDate(expirationDate),
  boxConfigurations: boxConfigurations.map(mapDraftShippingBoxConfiguration),
  shipmentQuantity
})

export const initShippingConfigurations = (products) => {
  let shippingConfigurations = []
  let idGen = IdGen()
  products.forEach(({id, name, quantitiesToSend}) => {
    quantitiesToSend.forEach((qTs) => {
      shippingConfigurations.push(mapDraftShippingFromApi({
        productId: id,
        productName: name,
        lotNumber: qTs.lotNumber,
        expirationDate: qTs.expirationDate,
        boxConfigurations: [mapDraftShippingBoxConfiguration(0, 0, 0, 0, 0, 0)],
        shipmentQuantity: qTs.amount
      }, idGen.next().value))
    })
  })
  return shippingConfigurations
}

const mapBoxConfiguration = ({
  quantityPerBox,
  numberOfBoxes,
  length,
  width,
  height,
  weight
  }) => ({
    quantityPerBox,
    numberOfBoxes,
    length,
    width,
    height,
    weight: mergePoundsAndOuncesIntoWeight(weight)
  })

const mapShippingConfigurationToApi = ({
  productId,
  lotNumber,
  expirationDate,
  boxConfigurations
}) => ({
  productId,
  lotNumber,
  expirationDate,
  boxConfigurations: boxConfigurations.map(mapBoxConfiguration)
})

export const mapShippingConfigurationsToApi = ({
  shippingConfigurations,
  id
}) => ({
  data: {
    shippingConfigurations: shippingConfigurations.map(mapShippingConfigurationToApi)
  },
  id
})

const mapMultiSKUBoxProductConfigurationsFromApi = (products, numberOfBoxes) => ({
  id,
  quantity,
  productId,
  lotNumber,
  expirationDate,
  length,
  width,
  height,
  weight
}) => {
  let product = products.find(p => p.id === productId)
  return {
    id,
    quantityPerBox: quantity,
    product: {
      ...product,
      lotInformation: {
        lotNumber,
        expirationDate: formatDate(expirationDate)
      }
    },
    numberOfBoxes,
    length,
    width,
    height,
    weight: splitWeightInPoundsAndOunces(weight)
  }
}

export const mapDraftMultiSkuShippingFromApi = (products) => ({
  id,
  numberOfBoxes,
  multiSKUBoxProductConfigurations
}) => {
  const idGen = IdGen()
  const mapper = mapMultiSKUBoxProductConfigurationsFromApi(products, numberOfBoxes)
  return {
    id,
    skuConfigurations: multiSKUBoxProductConfigurations.map(msbpc => mapper({ ...msbpc, id: idGen.next().value }))
  }
}

export const mapDraftMultiSkuShippingConfigurationsFromApi = (multiSKUShippingConfigurations, products) => {
  const idGen = IdGen()
  const mapper = mapDraftMultiSkuShippingFromApi(products)
  return multiSKUShippingConfigurations && multiSKUShippingConfigurations.map(mssc => mapper({ ...mssc, id: idGen.next().value }))
}

const mapMultiSkuBoxProductConfiguration = ({
  product,
  quantityPerBox,
  length,
  width,
  height,
  weight
}) => ({
  quantity: quantityPerBox,
  productId: product.id,
  lotNumber: product.lotInformation && product.lotInformation.lotNumber,
  expirationDate: product.lotInformation && product.lotInformation.expirationDate,
  length,
  width,
  height,
  weight: mergePoundsAndOuncesIntoWeight(weight)
})

const mapMultiSkuShippingConfigurationToApi = ({
  skuConfigurations
}) => ({
  numberOfBoxes: skuConfigurations.length > 0 ? skuConfigurations[0].numberOfBoxes : 0,
  multiSKUBoxProductConfigurations: skuConfigurations.map(mapMultiSkuBoxProductConfiguration)
})

export const mapMultiSkuShippingConfigurationsToApi = ({
  id,
  multiSKUShippingConfigurations
}) => ({
  data: {
    multiSKUShippingConfigurations: multiSKUShippingConfigurations.map(mapMultiSkuShippingConfigurationToApi)
  },
  id
})
